/**
 * CLE-2811 — the contract page's view of its contract models: the banner under the header (what the
 * latest contract model is doing, with a link to it), "Open latest model" in place of "Model with AI" once
 * one exists, and the attachment upload that can start one. Pure, so each rule is tested without a store
 * or a DOM.
 */

import {
  CONTRACT_MODEL_FACTS_READY,
  CONTRACT_MODEL_FOUND_NOTHING,
  CONTRACT_MODEL_READING,
  contractModelFailed,
  contractModelFound,
} from "pages/ContractModelDrafts/copy";
import {
  humanizeRunError,
  isFailedRun,
} from "pages/ContractModelDrafts/runState";
import {
  ALLOWED_UPLOAD_EXTENSIONS,
  describeProgress,
  formatCounter,
  generationElapsedMs,
  isProcessing,
} from "pages/ContractModelDrafts/utils";
import {
  IContractLatestModelRun,
  IDraftRunProgress,
} from "store/queries/contractModelDrafts";

/** Where a contract model lives. */
export const contractModelPath = (runId: string): string =>
  `/contract-model-drafts/${runId}`;

// ---------------------------------------------------------------------------------------------
// The banner
// ---------------------------------------------------------------------------------------------

export type ContractModelBannerKind =
  | "reading"
  | "facts_ready"
  | "found"
  | "failed";

export interface IContractModelBanner {
  kind: ContractModelBannerKind;
  text: string;
  severity: "info" | "success" | "error";
  /** A pass in flight with nothing saved yet: show what the worker is doing and for how long. */
  live: boolean;
}

/**
 * What the banner says about the latest contract model, or null for no banner:
 * - a pass in flight → "AI is reading this contract…" (with the live phase and elapsed time), or, once
 *   the facts are saved (`interimAt`), "Facts ready — schedules still extracting";
 * - `completed` / `awaiting_input` → "AI found the contract facts — review and save (N items)";
 * - `failed` / `dead` → "The AI pass failed: <why, in plain words>";
 * - `canceled`, a `collecting` run that was never started, or no model at all → nothing.
 */
export const contractModelBannerOf = (
  latest: IContractLatestModelRun | null | undefined
): IContractModelBanner | null => {
  if (!latest) return null;
  if (isProcessing(latest.status)) {
    return latest.interimAt
      ? {
          kind: "facts_ready",
          text: CONTRACT_MODEL_FACTS_READY,
          severity: "info",
          live: false,
        }
      : {
          kind: "reading",
          text: CONTRACT_MODEL_READING,
          severity: "info",
          live: true,
        };
  }
  if (latest.status === "completed" || latest.status === "awaiting_input") {
    const items = Math.max(0, latest.recommendationCount ?? 0);
    return {
      kind: "found",
      text:
        items > 0 ? contractModelFound(items) : CONTRACT_MODEL_FOUND_NOTHING,
      severity: "success",
      live: false,
    };
  }
  if (isFailedRun(latest.status)) {
    return {
      kind: "failed",
      text: contractModelFailed(humanizeRunError(latest.lastError).summary),
      severity: "error",
      live: false,
    };
  }
  return null;
};

/**
 * The slim response's `interimAt`: a stamp, null for "not set", undefined when an older API sends no such
 * field — which is then never read as a change.
 */
const slimInterimOf = (slim: {
  interimAt?: string | null;
}): string | null | undefined => slim.interimAt;

/** "Open latest model" replaces "Model with AI" when the contract has a model that is not canceled. */
export const hasOpenableLatest = (
  latest: IContractLatestModelRun | null | undefined
): latest is IContractLatestModelRun =>
  !!latest && latest.status !== "canceled";

/**
 * The latest model as the banner shows it: the slim progress poll's status and `interimAt` laid over it,
 * only while BOTH say a pass is in flight — so leaving a pass is the latest model's own call, and the
 * banner never shows "found (0 items)" for the beat before the refetch brings the count.
 */
export const liveLatestOf = (
  latest: IContractLatestModelRun | null | undefined,
  slim: IDraftRunProgress | null
): IContractLatestModelRun | null => {
  if (!latest) return null;
  if (
    !slim ||
    slim.id !== latest.id ||
    !isProcessing(latest.status) ||
    !isProcessing(slim.status)
  )
    return latest;
  const interimAt = slimInterimOf(slim);
  return {
    ...latest,
    status: slim.status,
    interimAt: interimAt === undefined ? latest.interimAt : interimAt,
  };
};

/**
 * Refetch the latest model when the slim poll says its pass has ended (the banner needs the final status
 * and count) or its facts were just saved. Only while the latest model is itself in flight, so a stale
 * last tick can never refetch in a loop.
 */
export const shouldRefetchLatest = (
  latest: IContractLatestModelRun | null | undefined,
  slim: IDraftRunProgress | null
): boolean => {
  if (!latest || !slim || slim.id !== latest.id) return false;
  if (!isProcessing(latest.status)) return false;
  if (!isProcessing(slim.status)) return true;
  const interimAt = slimInterimOf(slim);
  return interimAt !== undefined && interimAt !== (latest.interimAt ?? null);
};

/** The banner's live line while a pass reads: what the worker is doing, and how long it has been going. */
export const liveProgressLine = (
  slim: Pick<
    IDraftRunProgress,
    "status" | "stage" | "progress" | "startedAt" | "updatedAt"
  >,
  nowMs: number
): { phase: string; elapsed: string | null } => {
  const elapsedMs = generationElapsedMs(slim, nowMs);
  return {
    phase: describeProgress(slim.status, slim.progress, nowMs, slim.stage),
    elapsed: elapsedMs == null ? null : formatCounter(elapsedMs),
  };
};

// ---------------------------------------------------------------------------------------------
// The attachment upload
// ---------------------------------------------------------------------------------------------

/** What the contract's attachment upload accepts (the same list as the Edit and Create contract forms). */
export const CONTRACT_ATTACHMENT_ACCEPT =
  ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.png,.jpg,.jpeg,.gif,.tif,.tiff,.bmp,.webp,.ppt,.pptx,.odt,.ods,.odp,.zip";

/** Whether the AI can read a file, by its extension — the worker's list. clear-api starts nothing for any other. */
export const isAiReadable = (fileName: string): boolean => {
  const parts = fileName.toLowerCase().split(".");
  return (
    parts.length > 1 &&
    ALLOWED_UPLOAD_EXTENSIONS.includes(parts[parts.length - 1])
  );
};

/**
 * The uploads to make, in order, one at a time. When she asked for the AI, the last file it can READ goes
 * up last and alone carries `extractWithAi`: clear-api starts the contract model from that upload — after
 * every other file is attached, so the model is seeded with all of them — and starts nothing for a file
 * the worker cannot read (an image), which is why the flag never rides on one. No readable file, no flag.
 */
export const attachmentUploadPlan = (
  files: File[],
  extractWithAi: boolean
): { file: File; extractWithAi: boolean }[] => {
  const flagged = extractWithAi
    ? files.map((f) => isAiReadable(f.name)).lastIndexOf(true)
    : -1;
  const plain = files
    .filter((_, i) => i !== flagged)
    .map((file) => ({ file, extractWithAi: false }));
  return flagged < 0
    ? plain
    : [...plain, { file: files[flagged], extractWithAi: true }];
};

/** The contract model an upload started, from its response; null when it started none. */
export const modelRunIdOf = (result: unknown): string | null => {
  if (!result || typeof result !== "object") return null;
  const { modelRunId, data } = result as {
    modelRunId?: unknown;
    data?: { modelRunId?: unknown } | null;
  };
  if (typeof modelRunId === "string" && modelRunId) return modelRunId;
  const nested = data?.modelRunId;
  return typeof nested === "string" && nested ? nested : null;
};

export interface IUploadOutcome {
  fileName: string;
  ok: boolean;
  modelRunId: string | null;
}

export interface IUploadSummary {
  uploaded: number;
  /** The files that did not go up, by name. */
  failed: string[];
  /** The contract model the upload started, if any. */
  modelRunId: string | null;
  /** She asked for the AI, files went up, and no contract model came back — say so, rather than nothing. */
  aiNotStarted: boolean;
  /** …because none of the files was one the AI can read, so nothing asked it to start. */
  aiNothingToRead: boolean;
}

export const summarizeUploads = (
  outcomes: IUploadOutcome[],
  ai: { requested: boolean; flagSent: boolean }
): IUploadSummary => {
  const uploaded = outcomes.filter((o) => o.ok).length;
  const modelRunId =
    outcomes.map((o) => o.modelRunId).find((id): id is string => !!id) ?? null;
  const notStarted = ai.requested && uploaded > 0 && !modelRunId;
  return {
    uploaded,
    failed: outcomes.filter((o) => !o.ok).map((o) => o.fileName),
    modelRunId,
    aiNotStarted: notStarted,
    aiNothingToRead: notStarted && !ai.flagSent,
  };
};

// ---------------------------------------------------------------------------------------------
// "Extract contract facts with AI after upload", remembered per browser
// ---------------------------------------------------------------------------------------------

export const EXTRACT_WITH_AI_STORAGE_KEY =
  "clearhealth.contractUpload.extractWithAi";

/** `localStorage`, or null where touching it throws (a sandboxed frame, storage disabled). */
const browserStorage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

/** Her last choice; ON when she has never chosen, or when the browser will not say. */
export const readExtractWithAiPreference = (
  storage: Pick<Storage, "getItem"> | null = browserStorage()
): boolean => {
  try {
    return storage?.getItem(EXTRACT_WITH_AI_STORAGE_KEY) !== "false";
  } catch {
    return true;
  }
};

/** Remember her choice. A browser that refuses (private mode, quota) just does not remember it. */
export const writeExtractWithAiPreference = (
  value: boolean,
  storage: Pick<Storage, "setItem"> | null = browserStorage()
): void => {
  try {
    storage?.setItem(EXTRACT_WITH_AI_STORAGE_KEY, value ? "true" : "false");
  } catch {
    // Not remembered — the checkbox still works for this upload.
  }
};
